import React, { useState } from 'react';
import { ChevronRight, ChevronDown, TrendingUp, TrendingDown, AlertCircle, DollarSign, Zap, Package, X, FileText, AlertTriangle, Brain, Cloud, Download, Settings, Lightbulb, CheckCircle } from 'lucide-react';
import { AppBadge } from '../components/base/AppBadge';
import { MiniSparkline } from '../components/charts/MiniSparkline';
import { CostOverviewChart } from '../components/charts/CostOverviewChart';
import { PeriodSelector } from '../components/PeriodSelector';
type CostTab = 'provider' | 'service' | 'budget';
interface ProviderCost {
  id: string;
  name: string;
  costMonth: number;
  percentTotal: number;
  efficiency: number;
  budget: number;
  consumed: number;
  trend: number;
  isTopSpender?: boolean;
  isTopSaver?: boolean;
}
interface ServiceCost {
  id: string;
  name: string;
  provider: string;
  vendor: string;
  costMonth: number;
  efficiencyGrade: 'A' | 'B' | 'C';
  requests: number;
  costPerRequest: number;
  trend: number;
  compliance: 'green' | 'amber';
  isTopSpender?: boolean;
  isTopSaver?: boolean;
}
interface BudgetLine {
  id: string;
  name: string;
  spent: number;
  budget: number;
  percent: number;
}
interface Alert {
  id: string;
  severity: 'amber' | 'red' | 'blue';
  service: string;
  provider?: string;
  vendor?: string;
  metric: string;
  metricLabel: string;
  message: string;
}
const PROVIDER_DATA: ProviderCost[] = [{
  id: 'p1',
  name: 'OpenAI',
  costMonth: 1580,
  percentTotal: 68,
  efficiency: 0.042,
  budget: 2000,
  consumed: 79,
  trend: 8,
  isTopSpender: true
}, {
  id: 'p2',
  name: 'Anthropic',
  costMonth: 520,
  percentTotal: 22,
  efficiency: 0.038,
  budget: 800,
  consumed: 65,
  trend: 12
}, {
  id: 'p3',
  name: 'Google Gemini',
  costMonth: 240,
  percentTotal: 10,
  efficiency: 0.035,
  budget: 400,
  consumed: 60,
  trend: -3,
  isTopSaver: true
}];
const SERVICE_DATA: ServiceCost[] = [{
  id: 's1',
  name: 'Customer Assistant',
  provider: 'OpenAI',
  vendor: 'Dmood',
  costMonth: 680,
  efficiencyGrade: 'B',
  requests: 12500,
  costPerRequest: 0.054,
  trend: 5,
  compliance: 'green'
}, {
  id: 's2',
  name: 'Smart Matcher',
  provider: 'Anthropic',
  vendor: 'Acme Dev',
  costMonth: 420,
  efficiencyGrade: 'A',
  requests: 8200,
  costPerRequest: 0.051,
  trend: -2,
  compliance: 'green',
  isTopSaver: true
}, {
  id: 's3',
  name: 'Content Moderator',
  provider: 'OpenAI',
  vendor: 'Internal',
  costMonth: 340,
  efficiencyGrade: 'C',
  requests: 15600,
  costPerRequest: 0.022,
  trend: 34,
  compliance: 'amber',
  isTopSpender: true
}, {
  id: 's4',
  name: 'AI Tutor',
  provider: 'Google Gemini',
  vendor: 'Dmood',
  costMonth: 240,
  efficiencyGrade: 'A',
  requests: 4800,
  costPerRequest: 0.05,
  trend: -1,
  compliance: 'green'
}];
const BUDGET_DATA: BudgetLine[] = [{
  id: 'b1',
  name: 'Customer Care - Interno',
  spent: 1200,
  budget: 1500,
  percent: 80
}, {
  id: 'b2',
  name: 'Marketing - Fornitore Acme',
  spent: 1450,
  budget: 1400,
  percent: 104
}, {
  id: 'b3',
  name: 'HR & Training - Interno',
  spent: 680,
  budget: 1000,
  percent: 68
}];
const ALERTS: Alert[] = [{
  id: 'a1',
  severity: 'amber',
  service: 'Content Moderator',
  provider: 'OpenAI',
  vendor: 'Internal',
  metric: '+34%',
  metricLabel: 'Aumento costi',
  message: 'Incremento significativo dei costi questa settimana'
}, {
  id: 'a2',
  severity: 'red',
  service: 'Marketing',
  vendor: 'Acme',
  metric: '€1.450 / €1.400',
  metricLabel: 'Budget superato',
  message: 'Linea di budget superata del 4%'
}, {
  id: 'a3',
  severity: 'blue',
  service: 'Smart Matcher',
  provider: 'Anthropic',
  vendor: 'Acme Dev',
  metric: '3x',
  metricLabel: 'Costo GPT-4 Turbo vs GPT-4o',
  message: 'Opportunità di ottimizzazione: valutare switch a GPT-4o per lo stesso task'
}];
const FILTERS = ['Tutti', 'Solo Alert', 'Compliance OK', 'Budget Superato'];
export function CostManagementPage() {
  const [activeTab, setActiveTab] = useState<CostTab>('provider');
  const [activeFilter, setActiveFilter] = useState('Tutti');
  const [isAlertDrawerOpen, setIsAlertDrawerOpen] = useState(false);
  const getBarColor = (percent: number) => {
    if (percent > 100) return 'bg-red-500';
    if (percent >= 80) return 'bg-amber-500';
    return 'bg-[#8eff01]';
  };
  const getDotColor = (severity: Alert['severity']) => {
    if (severity === 'red') return 'bg-red-500';
    if (severity === 'amber') return 'bg-amber-500';
    return 'bg-blue-500';
  };
  const getProviderIcon = (providerName: string) => {
    switch (providerName) {
      case 'OpenAI':
        return <img src="https://cdn.magicpatterns.com/uploads/1uRgHdJpNKcdeM7NG8hvjt/image.png" alt="OpenAI" className="w-7 h-7 object-contain" data-id="element-2119" />;
      case 'Anthropic':
        return <img src="https://cdn.magicpatterns.com/uploads/aLJFmhZ7nGDmPeNzEd4tUL/anthropic-1.svg" alt="Anthropic" className="w-7 h-7 object-contain" data-id="element-2120" />;
      case 'Google Gemini':
        return <img src="https://cdn.magicpatterns.com/uploads/6ACcFT2ZyoV6cbNrxRM4jT/Google_Gemini_icon_2025.svg.png" alt="Google Gemini" className="w-7 h-7 object-contain" data-id="element-2121" />;
      default:
        return <Package className="w-7 h-7 text-white/70" data-id="element-2122" />;
    }
  };
  // Filter Data
  const filteredProviders = PROVIDER_DATA.filter(p => {
    if (activeFilter === 'Solo Alert') return p.consumed >= 80;
    if (activeFilter === 'Budget Superato') return p.consumed > 100;
    if (activeFilter === 'Compliance OK') return p.consumed < 80;
    return true;
  });
  const filteredServices = SERVICE_DATA.filter(s => {
    if (activeFilter === 'Solo Alert') return s.compliance === 'amber';
    if (activeFilter === 'Compliance OK') return s.compliance === 'green';
    if (activeFilter === 'Budget Superato') return false; // Services don't have direct budget in this mock
    return true;
  });
  const filteredBudgets = BUDGET_DATA.filter(b => {
    if (activeFilter === 'Solo Alert') return b.percent >= 80;
    if (activeFilter === 'Budget Superato') return b.percent > 100;
    if (activeFilter === 'Compliance OK') return b.percent < 80;
    return true;
  });
  return <div className="min-h-screen bg-[#121212]" data-id="element-2123">
      {/* Header Section */}
      <div data-id="element-2124">
        <div className="pt-6 px-4 md:px-8 pb-4 flex flex-col md:flex-row md:items-center justify-between gap-4" data-id="element-2125">
          <h1 className="font-semibold text-white tracking-tight text-[20px]" data-id="element-2126">
            Cost Management
          </h1>

          <div className="flex items-center gap-3" data-id="element-2127">
            <PeriodSelector data-id="element-2128" />

            <div className="flex items-center gap-1 border-r border-white/[0.08] pr-3" data-id="element-2129">
              <button title="Export CSV" className="p-2 text-white/70 hover:text-white bg-white/[0.03] hover:bg-white/[0.06] rounded-lg transition-all" data-id="element-2130">
                <Download className="w-4 h-4" data-id="element-2131" />
              </button>
              <button title="Report Mensile" className="p-2 text-white/70 hover:text-white bg-white/[0.03] hover:bg-white/[0.06] rounded-lg transition-all" data-id="element-2132">
                <FileText className="w-4 h-4" data-id="element-2133" />
              </button>
              <button title="Configura Budget" className="p-2 text-white/70 hover:text-white bg-white/[0.03] hover:bg-white/[0.06] rounded-lg transition-all" data-id="element-2134">
                <Settings className="w-4 h-4" data-id="element-2135" />
              </button>
            </div>

            {ALERTS.length > 0 && <button onClick={() => setIsAlertDrawerOpen(true)} className="relative flex items-center gap-2 px-4 py-2 bg-white/[0.03] hover:bg-white/[0.06] border border-white/[0.08] rounded-full transition-all" data-id="element-2136">
                <AlertTriangle className="w-4 h-4 text-white/70" data-id="element-2137" />
                <span className="text-[13px] font-medium text-white hidden sm:inline" data-id="element-2138">
                  Segnalazioni
                </span>
                <span className="flex items-center justify-center min-w-[20px] h-5 px-1.5 text-[11px] font-semibold rounded-full text-[#FFFFFF] bg-[#FF0026]" data-id="element-2139">
                  {ALERTS.length}
                </span>
              </button>}
          </div>
        </div>
      </div>

      {/* KPI Cards Section */}
      <div data-id="element-2140">
        <div className="px-4 md:px-8 py-4" data-id="element-2141">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-2142">
            {/* Spesa Totale */}
            <div className="bg-[#0a0a0a] border border-white/[0.08] rounded-lg p-3" data-id="element-2143">
              <div className="flex items-center gap-2 mb-2" data-id="element-2144">
                <DollarSign className="w-4 h-4 text-white/40" data-id="element-2145" />
                <span className="text-[11px] text-white/40 uppercase tracking-wider font-medium" data-id="element-2146">
                  Spesa Totale
                </span>
              </div>
              <div className="flex items-baseline gap-2" data-id="element-2147">
                <p className="font-bold text-white text-[20px]" data-id="element-2148">€2.340</p>
              </div>
            </div>

            {/* Provider Attivi */}
            <div className="bg-[#0a0a0a] border border-white/[0.08] rounded-lg p-3" data-id="element-2149">
              <div className="flex items-center gap-2 mb-2" data-id="element-2150">
                <Zap className="w-4 h-4 text-white/40" data-id="element-2151" />
                <span className="text-[11px] text-white/40 uppercase tracking-wider font-medium" data-id="element-2152">
                  Provider
                </span>
              </div>
              <p className="font-bold text-white text-[20px]" data-id="element-2153">3/10</p>
            </div>

            {/* Servizi Attivi */}
            <div className="bg-[#0a0a0a] border border-white/[0.08] rounded-lg p-3" data-id="element-2154">
              <div className="flex items-center gap-2 mb-2" data-id="element-2155">
                <Package className="w-4 h-4 text-white/40" data-id="element-2156" />
                <span className="text-[11px] text-white/40 uppercase tracking-wider font-medium" data-id="element-2157">
                  Servizi
                </span>
              </div>
              <div className="flex items-baseline gap-2" data-id="element-2158">
                <p className="font-bold text-white text-[20px]" data-id="element-2159">8/16</p>
              </div>
            </div>

            {/* Linee di Budget */}
            <div className="bg-[#0a0a0a] border border-white/[0.08] rounded-lg p-3" data-id="element-2160">
              <div className="flex items-center gap-2 mb-2" data-id="element-2161">
                <FileText className="w-4 h-4 text-white/40" data-id="element-2162" />
                <span className="text-[11px] text-white/40 uppercase tracking-wider font-medium" data-id="element-2163">
                  Linee Budget
                </span>
              </div>
              <div className="flex items-baseline gap-2" data-id="element-2164">
                <p className="font-bold text-white text-[20px]" data-id="element-2165">
                  {BUDGET_DATA.length}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Section */}
      <div className="px-4 md:px-8 py-6" data-id="element-2166">
        {/* Insights Section */}
        <div className="mb-8 bg-white/[0.02] border border-white/[0.08] rounded-lg p-4" data-id="element-2167">
          <h2 className="text-[14px] font-semibold text-white mb-3 flex items-center gap-2" data-id="element-2168">
            <Lightbulb className="w-4 h-4 text-[#8eff01]" data-id="element-2169" />
            Insights & Raccomandazioni
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-2170">
            <div className="bg-[#0a0a0a] p-3 rounded-lg border border-white/[0.06]" data-id="element-2171">
              <p className="text-[12px] text-white/70 leading-relaxed" data-id="element-2172">
                <strong className="text-white font-medium" data-id="element-2173">
                  Smart Matcher
                </strong>{' '}
                potrebbe risparmiare €126/mese passando a GPT-4o mantenendo le
                stesse performance.
              </p>
            </div>
            <div className="bg-[#0a0a0a] p-3 rounded-lg border border-white/[0.06]" data-id="element-2174">
              <p className="text-[12px] text-white/70 leading-relaxed" data-id="element-2175">
                Spesa <strong className="text-white font-medium" data-id="element-2176">OpenAI</strong>{' '}
                +34% questa settimana. Verifica l'utilizzo del Content
                Moderator.
              </p>
            </div>
            <div className="bg-[#0a0a0a] p-3 rounded-lg border border-white/[0.06]" data-id="element-2177">
              <p className="text-[12px] text-white/70 leading-relaxed" data-id="element-2178">
                <strong className="text-white font-medium" data-id="element-2179">2 servizi</strong>{' '}
                sono sotto budget del 40%. Hai capacità disponibile per nuovi
                use case.
              </p>
            </div>
          </div>
        </div>

        {/* Cost Overview Chart */}

        {/* Section Header */}
        <div className="mb-5" data-id="element-2180">
          <h2 className="text-[16px] font-semibold text-white mb-1" data-id="element-2181">
            Dettaglio Costi
          </h2>
        </div>

        {/* Tab Navigation */}
        <div className="mb-4" data-id="element-2182">
          <div className="flex gap-8 border-b border-white/[0.06] overflow-x-auto" data-id="element-2183">
            <button onClick={() => setActiveTab('provider')} className={`relative px-1 pb-3 text-[14px] font-medium transition-all whitespace-nowrap ${activeTab === 'provider' ? 'text-white' : 'text-white/40 hover:text-white/70'}`} data-id="element-2184">
              Per Provider
              {activeTab === 'provider' && <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-[#8eff01] rounded-full" data-id="element-2185" />}
            </button>
            <button onClick={() => setActiveTab('service')} className={`relative px-1 pb-3 text-[14px] font-medium transition-all whitespace-nowrap ${activeTab === 'service' ? 'text-white' : 'text-white/40 hover:text-white/70'}`} data-id="element-2186">
              Per Servizio
              {activeTab === 'service' && <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-[#8eff01] rounded-full" data-id="element-2187" />}
            </button>
            <button onClick={() => setActiveTab('budget')} className={`relative px-1 pb-3 text-[14px] font-medium transition-all whitespace-nowrap ${activeTab === 'budget' ? 'text-white' : 'text-white/40 hover:text-white/70'}`} data-id="element-2188">
              Linee di Budget
              {activeTab === 'budget' && <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-[#8eff01] rounded-full" data-id="element-2189" />}
            </button>
          </div>
        </div>

        {/* Filter Chips */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2" data-id="element-2190">
          {FILTERS.map(filter => <button key={filter} onClick={() => setActiveFilter(filter)} className={`px-3 py-1.5 rounded-full text-[12px] font-medium transition-all whitespace-nowrap ${activeFilter === filter ? 'bg-[#8eff01] text-black' : 'bg-white/[0.03] text-white/40 hover:text-white/70 hover:bg-white/[0.06] border border-white/[0.05]'}`} data-id="element-2191">
              {filter}
            </button>)}
        </div>

        {/* Tab Content */}
        <div className="bg-[#0a0a0a] border border-white/[0.08] rounded-lg overflow-hidden" data-id="element-2192">
          {activeTab === 'provider' && <div className="overflow-x-auto" data-id="element-2193">
              <table className="w-full" data-id="element-2194">
                <thead className="bg-white/[0.03] border-b border-white/[0.08]" data-id="element-2195">
                  <tr data-id="element-2196">
                    <th className="px-6 py-4 text-left text-[11px] font-semibold text-white/40 uppercase tracking-wider" data-id="element-2197">
                      Provider
                    </th>
                    <th className="px-6 py-4 text-left text-[11px] font-semibold text-white/40 uppercase tracking-wider" data-id="element-2198">
                      Spesa Mese
                    </th>
                    <th className="px-6 py-4 text-left text-[11px] font-semibold text-white/40 uppercase tracking-wider" data-id="element-2199">
                      % Totale
                    </th>
                    <th className="px-6 py-4 text-left text-[11px] font-semibold text-white/40 uppercase tracking-wider" data-id="element-2200">
                      Efficienza
                    </th>
                    <th className="px-6 py-4 text-left text-[11px] font-semibold text-white/40 uppercase tracking-wider" data-id="element-2201">
                      Budget
                    </th>
                    <th className="px-6 py-4 text-left text-[11px] font-semibold text-white/40 uppercase tracking-wider" data-id="element-2202">
                      Consumato
                    </th>
                    <th className="px-6 py-4 text-left text-[11px] font-semibold text-white/40 uppercase tracking-wider" data-id="element-2203">
                      Trend 7gg
                    </th>
                    <th className="px-6 py-4" data-id="element-2204"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/[0.05]" data-id="element-2205">
                  {filteredProviders.map(provider => <tr key={provider.id} className={`hover:bg-white/[0.03] transition-colors ${provider.isTopSpender ? 'border-l-2 border-l-amber-500' : provider.isTopSaver ? 'border-l-2 border-l-green-500' : 'border-l-2 border-l-transparent'}`} data-id="element-2206">
                      <td className="px-6 py-4" data-id="element-2207">
                        <div className="flex items-center gap-3" data-id="element-2208">
                          <div className="flex-shrink-0" data-id="element-2209">
                            {getProviderIcon(provider.name)}
                          </div>
                          <div className="flex flex-col" data-id="element-2210">
                            <span className="text-[13px] font-medium text-white flex items-center gap-2" data-id="element-2211">
                              {provider.name}
                              {provider.isTopSpender && <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-amber-500/10 text-amber-500 uppercase tracking-wider" data-id="element-2212">
                                  Top Spender
                                </span>}
                              {provider.isTopSaver && <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-green-500/10 text-green-500 uppercase tracking-wider" data-id="element-2213">
                                  Top Saver
                                </span>}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-[13px] text-white/70" data-id="element-2214">
                        €{provider.costMonth.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 text-[13px] text-white/70" data-id="element-2215">
                        {provider.percentTotal}%
                      </td>
                      <td className="px-6 py-4" data-id="element-2216">
                        <div className="flex flex-col" data-id="element-2217">
                          <span className="text-[13px] text-white/70" data-id="element-2218">
                            €{provider.efficiency}
                          </span>
                          <span className="text-[10px] text-white/30" data-id="element-2219">
                            per 1K token
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-[13px] text-white/70" data-id="element-2220">
                        €{provider.budget.toLocaleString()}
                      </td>
                      <td className="px-6 py-4" data-id="element-2221">
                        <div className="flex items-center gap-3" data-id="element-2222">
                          <span className={`text-[13px] font-medium w-9 ${provider.consumed >= 80 ? 'text-amber-400' : 'text-white/70'}`} data-id="element-2223">
                            {provider.consumed}%
                          </span>
                          <div className="w-16 h-1.5 bg-white/[0.08] rounded-full overflow-hidden" data-id="element-2224">
                            <div className={`h-full ${getBarColor(provider.consumed)} transition-all`} style={{
                        width: `${Math.min(provider.consumed, 100)}%`
                      }} data-id="element-2225" />
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4" data-id="element-2226">
                        <div className={`flex items-center text-[13px] font-medium ${provider.trend > 0 ? 'text-amber-400' : 'text-green-400'}`} data-id="element-2227">
                          {provider.trend > 0 ? <TrendingUp className="w-3.5 h-3.5 mr-1" data-id="element-2228" /> : <TrendingDown className="w-3.5 h-3.5 mr-1" data-id="element-2229" />}
                          {provider.trend > 0 ? '+' : ''}
                          {provider.trend}%
                        </div>
                      </td>
                      <td className="px-6 py-4 text-right" data-id="element-2230">
                        <button className="p-2 text-white/30 hover:text-white/70 hover:bg-white/[0.05] rounded-lg transition-all" data-id="element-2231">
                          <ChevronRight className="w-4 h-4" data-id="element-2232" />
                        </button>
                      </td>
                    </tr>)}
                  {filteredProviders.length === 0 && <tr data-id="element-2233">
                      <td colSpan={8} className="px-6 py-8 text-center text-[13px] text-white/40" data-id="element-2234">
                        Nessun provider trovato per i filtri selezionati
                      </td>
                    </tr>}
                </tbody>
              </table>
            </div>}

          {activeTab === 'service' && <div className="overflow-x-auto" data-id="element-2235">
              <table className="w-full" data-id="element-2236">
                <thead className="bg-white/[0.03] border-b border-white/[0.08]" data-id="element-2237">
                  <tr data-id="element-2238">
                    <th className="px-6 py-4 text-left text-[11px] font-semibold text-white/40 uppercase tracking-wider" data-id="element-2239">
                      Servizio
                    </th>
                    <th className="px-6 py-4 text-left text-[11px] font-semibold text-white/40 uppercase tracking-wider" data-id="element-2240">
                      Provider
                    </th>
                    <th className="px-6 py-4 text-left text-[11px] font-semibold text-white/40 uppercase tracking-wider" data-id="element-2241">
                      Costo Mese
                    </th>
                    <th className="px-6 py-4 text-left text-[11px] font-semibold text-white/40 uppercase tracking-wider" data-id="element-2242">
                      Efficienza
                    </th>
                    <th className="px-6 py-4 text-left text-[11px] font-semibold text-white/40 uppercase tracking-wider" data-id="element-2243">
                      Requests
                    </th>
                    <th className="px-6 py-4 text-left text-[11px] font-semibold text-white/40 uppercase tracking-wider" data-id="element-2244">
                      Trend
                    </th>
                    <th className="px-6 py-4 text-left text-[11px] font-semibold text-white/40 uppercase tracking-wider" data-id="element-2245">
                      Compliance
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/[0.05]" data-id="element-2246">
                  {filteredServices.map(service => <tr key={service.id} className={`hover:bg-white/[0.03] transition-colors ${service.isTopSpender ? 'border-l-2 border-l-amber-500' : service.isTopSaver ? 'border-l-2 border-l-green-500' : 'border-l-2 border-l-transparent'}`} data-id="element-2247">
                      <td className="px-6 py-4" data-id="element-2248">
                        <div className="flex flex-col" data-id="element-2249">
                          <span className="text-[13px] font-medium text-white flex items-center gap-2" data-id="element-2250">
                            {service.name}
                            {service.isTopSpender && <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-amber-500/10 text-amber-500 uppercase tracking-wider" data-id="element-2251">
                                Top Spender
                              </span>}
                            {service.isTopSaver && <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-green-500/10 text-green-500 uppercase tracking-wider" data-id="element-2252">
                                Top Saver
                              </span>}
                          </span>
                          <span className="text-[11px] text-white/40" data-id="element-2253">
                            {service.vendor}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-[13px] text-white/70" data-id="element-2254">
                        {service.provider}
                      </td>
                      <td className="px-6 py-4 text-[13px] text-white/70" data-id="element-2255">
                        €{service.costMonth}
                      </td>
                      <td className="px-6 py-4" data-id="element-2256">
                        <span className={`inline-flex items-center justify-center w-6 h-6 rounded text-[12px] font-bold ${service.efficiencyGrade === 'A' ? 'bg-green-500/10 text-green-500' : service.efficiencyGrade === 'B' ? 'bg-blue-500/10 text-blue-500' : 'bg-amber-500/10 text-amber-500'}`} data-id="element-2257">
                          {service.efficiencyGrade}
                        </span>
                      </td>
                      <td className="px-6 py-4" data-id="element-2258">
                        <div className="flex flex-col" data-id="element-2259">
                          <span className="text-[13px] text-white/70" data-id="element-2260">
                            {service.requests.toLocaleString()}
                          </span>
                          <span className="text-[10px] text-white/30" data-id="element-2261">
                            €{service.costPerRequest.toFixed(3)}/req
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4" data-id="element-2262">
                        <div className={`flex items-center text-[13px] font-medium ${service.trend > 0 ? 'text-amber-400' : 'text-green-400'}`} data-id="element-2263">
                          {service.trend > 0 ? <TrendingUp className="w-3.5 h-3.5 mr-1" data-id="element-2264" /> : <TrendingDown className="w-3.5 h-3.5 mr-1" data-id="element-2265" />}
                          {service.trend > 0 ? '+' : ''}
                          {service.trend}%
                        </div>
                      </td>
                      <td className="px-6 py-4" data-id="element-2266">
                        <AppBadge variant={service.compliance === 'green' ? 'green' : 'amber'} data-id="element-2267">
                          {service.compliance === 'green' ? 'OK' : 'Review'}
                        </AppBadge>
                      </td>
                    </tr>)}
                  {filteredServices.length === 0 && <tr data-id="element-2268">
                      <td colSpan={7} className="px-6 py-8 text-center text-[13px] text-white/40" data-id="element-2269">
                        Nessun servizio trovato per i filtri selezionati
                      </td>
                    </tr>}
                </tbody>
              </table>
            </div>}

          {activeTab === 'budget' && <div className="p-6 space-y-5" data-id="element-2270">
              {filteredBudgets.map(line => <div key={line.id} className="space-y-2" data-id="element-2271">
                  <div className="flex items-center justify-between" data-id="element-2272">
                    <p className="text-[13px] font-medium text-white" data-id="element-2273">
                      {line.name}
                    </p>
                    <div className="flex items-center gap-3" data-id="element-2274">
                      <span className="text-[12px] text-white/40" data-id="element-2275">
                        €{line.spent.toLocaleString()} / €
                        {line.budget.toLocaleString()}
                      </span>
                      <span className={`text-[13px] font-semibold ${line.percent > 100 ? 'text-red-500' : line.percent >= 80 ? 'text-amber-500' : 'text-[#8eff01]'}`} data-id="element-2276">
                        {line.percent}%
                      </span>
                    </div>
                  </div>
                  <div className="h-2 bg-white/[0.08] rounded-full overflow-hidden" data-id="element-2277">
                    <div className={`h-full transition-all ${getBarColor(line.percent)}`} style={{
                width: `${Math.min(line.percent, 100)}%`
              }} data-id="element-2278" />
                  </div>
                </div>)}
              {filteredBudgets.length === 0 && <div className="py-8 text-center text-[13px] text-white/40" data-id="element-2279">
                  Nessuna linea di budget trovata per i filtri selezionati
                </div>}
            </div>}
        </div>
      </div>

      {/* Alert Drawer */}
      <>
        {/* Backdrop */}
        <div className={`fixed inset-0 bg-black/60 backdrop-blur-sm z-40 transition-opacity duration-300 ease-in-out ${isAlertDrawerOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`} onClick={() => setIsAlertDrawerOpen(false)} data-id="element-2280" />

        {/* Drawer */}
        <div className={`fixed top-0 right-0 h-full w-full md:w-[480px] bg-[#121212] z-50 shadow-2xl overflow-y-auto transform transition-transform duration-300 ease-in-out ${isAlertDrawerOpen ? 'translate-x-0' : 'translate-x-full'}`} data-id="element-2281">
          {/* Drawer Header */}
          <div className="sticky top-0 bg-[#121212] border-b border-white/[0.06] px-6 py-5 flex items-center justify-between" data-id="element-2282">
            <div data-id="element-2283">
              <h2 className="text-[18px] font-semibold text-white" data-id="element-2284">
                Alert & Notifiche
              </h2>
              <p className="text-[12px] text-white/40 mt-0.5" data-id="element-2285">
                {ALERTS.length} notifiche attive
              </p>
            </div>
            <button onClick={() => setIsAlertDrawerOpen(false)} className="p-2 text-white/40 hover:text-white hover:bg-white/[0.08] rounded-lg transition-all" data-id="element-2286">
              <X className="w-5 h-5" data-id="element-2287" />
            </button>
          </div>

          {/* Drawer Content */}
          <div className="p-6 space-y-3" data-id="element-2288">
            {ALERTS.map(alert => <div key={alert.id} className="bg-[#0a0a0a] border border-white/[0.08] rounded-lg p-4" data-id="element-2289">
                <div className="flex items-start gap-3" data-id="element-2290">
                  <div className={`w-1.5 h-1.5 rounded-full ${getDotColor(alert.severity)} mt-1.5 flex-shrink-0`} data-id="element-2291" />
                  <div className="flex-1 space-y-2" data-id="element-2292">
                    {/* Header: Service + Provider/Vendor */}
                    <div className="flex items-center gap-2 flex-wrap" data-id="element-2293">
                      <span className="text-[13px] font-semibold text-white" data-id="element-2294">
                        {alert.service}
                      </span>
                      {alert.provider && <>
                          <span className="text-white/20" data-id="element-2295">·</span>
                          <span className="text-[11px] text-white/40" data-id="element-2296">
                            {alert.provider}
                          </span>
                        </>}
                      {alert.vendor && <>
                          <span className="text-white/20" data-id="element-2297">·</span>
                          <span className="text-[11px] text-white/40" data-id="element-2298">
                            {alert.vendor}
                          </span>
                        </>}
                    </div>

                    {/* Metric */}
                    <div className="flex items-baseline gap-1.5" data-id="element-2299">
                      <span className="text-[16px] font-bold text-white" data-id="element-2300">
                        {alert.metric}
                      </span>
                      <span className="text-[10px] text-white/30 uppercase tracking-wider" data-id="element-2301">
                        {alert.metricLabel}
                      </span>
                    </div>

                    {/* Message */}
                    <p className="text-[12px] text-white/40 leading-relaxed" data-id="element-2302">
                      {alert.message}
                    </p>
                  </div>
                </div>
              </div>)}
          </div>
        </div>
      </>
    </div>;
}